import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { DashboardCardCP } from 'app/screens/screen-home/components/DashboardCardCP'
import { DashboardFilterTP } from 'app/screens/screen-home/inner/DashboardFilterTP'
import { DashboardLinksUtils } from 'app/screens/screen-home/inner/DashboardLinksUtils'
import { ThemeProject } from 'config/theme/project/ThemeProject'
import { DashboardRequests } from 'submodules/nerit-aquatec-sdk/services/dashboard/DashboardRequests'
import { DashboardQAQCResponseDTO, DashboardQAQCValidationResponseDTO } from 'submodules/nerit-aquatec-sdk/services/dashboard/dtos/responses/DashboardQAQCResponseDTO'
import { ButtonCP } from 'submodules/nerit-framework-ui/common/components/button/ButtonCP'
import { RowCP } from 'submodules/nerit-framework-ui/common/components/grid/row/RowCP'
import { ColumnCP } from 'submodules/nerit-framework-ui/common/components/grid/column/ColumnCP'
import { TextCP } from 'submodules/nerit-framework-ui/common/components/text/TextCP'
import { NotificationHelper } from 'submodules/nerit-framework-ui/common/components/notification/inner/NotificationHelper'
import { RequestHelper } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/RequestHelper'
import { IApiReturn } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/types/IApiReturn'

interface ICPProps {
    filter: DashboardFilterTP
}

/**
 * Qualidade dos dados: percentual de amostras aprovadas no Balanço Iônico e na Avaliação de Anomalias (QAQC).
 */
export function DashboardQAQCCardCP(props: ICPProps): JSX.Element {

    const [qaqc, setQaqc] = useState<DashboardQAQCResponseDTO>()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    useEffect(() => { void load() }, [props.filter])

    /**
     */
    async function load(): Promise<void> {

        setIsLoading(true)
        try {
            const response = await RequestHelper.runRequest<IApiReturn<DashboardQAQCResponseDTO>>(
                DashboardRequests.getQAQC({
                    locationCodes: props.filter.locationCodes,
                    regulationCode: props.filter.regulationCode,
                    dateRange: props.filter.dateRange,
                }),
            )
            setQaqc(response.data.data)
        } catch {
            setQaqc(undefined)
            NotificationHelper.error(NotificationHelper.DEFAULT_ERROR_GET_MESSAGE)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <DashboardCardCP
            title={'Qualidade dos dados'}
            subtitle={'Validação QA/QC'}
            isLoading={isLoading}
            minHeight={320}
        >
            {
                !!qaqc &&
                <RowCP>
                    <ColumnCP size={12} xs={24} lg={12}>
                        <ValidationCP
                            title={'Balanço Iônico'}
                            result={qaqc.ionicBalance}
                            onOpen={DashboardLinksUtils.goToIonicBalance}
                        />
                    </ColumnCP>
                    <ColumnCP size={12} xs={24} lg={12}>
                        <ValidationCP
                            title={'Avaliação de Anomalias'}
                            result={qaqc.anomalyEvaluation}
                            onOpen={DashboardLinksUtils.goToAnomalyEvaluation}
                        />
                    </ColumnCP>
                </RowCP>
            }
        </DashboardCardCP>
    )
}

interface IValidationCPProps {
    title: string
    result: DashboardQAQCValidationResponseDTO
    onOpen: () => void
}

/**
 * Pizza de aprovadas x reprovadas de uma validação, com os totais ao lado.
 */
function ValidationCP(props: IValidationCPProps): JSX.Element {

    const approvedPercent = props.result.totalSamples > 0
        ? Math.round((props.result.totalApprovedSamples / props.result.totalSamples) * 100)
        : 0

    return (
        <ValidationWrapperSCP>
            <ValidationHeaderSCP>
                <TextCP text={props.title} strong={true}/>
                <ButtonCP type={'link'} icon={'arrow-right'} onClick={props.onOpen} tooltip={`Abrir ${props.title}`}/>
            </ValidationHeaderSCP>

            <ValidationBodySCP>
                <PieWrapperSCP>
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={{
                            chart: { type: 'pie', height: 140, width: 140, margin: 0 },
                            credits: { enabled: false },
                            exporting: { enabled: false },
                            title: {
                                text: props.result.totalSamples > 0 ? `${approvedPercent}%` : '-',
                                verticalAlign: 'middle',
                                y: 6,
                                style: { fontSize: '18px', fontWeight: '600' },
                            },
                            tooltip: { pointFormat: '<b>{point.y}</b> amostras' },
                            plotOptions: { pie: { dataLabels: { enabled: false }, innerSize: '65%' } },
                            series: [{
                                type: 'pie',
                                name: props.title,
                                data: props.result.totalSamples > 0
                                    ? [
                                        { name: 'Aprovadas', y: props.result.totalApprovedSamples, color: ThemeProject.success },
                                        { name: 'Reprovadas', y: props.result.totalRejectedSamples, color: ThemeProject.error },
                                    ]
                                    : [{ name: 'Sem amostras', y: 1, color: '#e8e8e8' }],
                            }],
                        }}
                    />
                </PieWrapperSCP>

                <div>
                    <TextCP text={`${props.result.totalSamples.toLocaleString('pt-BR')} amostras avaliadas`} strong={true} marginBottom={6}/>
                    <TextCP text={`${props.result.totalApprovedSamples.toLocaleString('pt-BR')} aprovadas`} color={ThemeProject.success} marginBottom={4}/>
                    <TextCP text={`${props.result.totalRejectedSamples.toLocaleString('pt-BR')} reprovadas`} color={ThemeProject.error}/>
                </div>
            </ValidationBodySCP>
        </ValidationWrapperSCP>
    )
}

const ValidationWrapperSCP = styled.div`
    padding: 8px 0;
`

const ValidationHeaderSCP = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`

/* Em cards estreitos os totais descem para baixo da pizza em vez de transbordar. */
const ValidationBodySCP = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
`

const PieWrapperSCP = styled.div`
    flex: 0 0 140px;
`
